import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Nav from 'react-bootstrap/Nav';
import NavItem from 'react-bootstrap/NavItem';
import NavLink from 'react-bootstrap/NavLink';
import Dropdown from 'react-bootstrap/Dropdown';
import { LinkContainer } from 'react-router-bootstrap';
import * as URL from '../../lib/router/url';
import { checkUserPermissions } from '../../modules/permissions/abilities/userHasPermissionV2';
import Can from '../../modules/permissions/abilities/Can';
import { Icon } from '../../modules/core';

// собрать листья раздела с учётом вложенных подгрупп { title, kids:[...] };
// скрытые (hide: true) не собираются: пункт выключен, но url и роут живы
const flattenLeaves = kids => kids.reduce(
  (acc, k) => acc.concat(Array.isArray(k.kids) ? k.kids : [k]),
  []
).filter(leaf => !leaf.hide);

// поиск-фильтр по меню: совпадение, если ВСЕ слова запроса есть в тексте (раздел + пункт)
const matchesTokens = (text, tokens) => {
  const haystack = (text || '').toLowerCase();
  return tokens.every(tok => haystack.includes(tok));
};

interface SideBarMenuProps {
  /** Профиль из Redux (state.auth.profile). */
  user?: any;
  /** Права из Redux (state.permissions.permissions). */
  permissions?: any[];
}

const SideBarMenu = ({ user, permissions }: SideBarMenuProps) => {
  const nav = URL.NAVI;
  const location = useLocation();

  // какой top-level раздел раскрыт (по sortOrder); null = все свёрнуты.
  // Аккордеон контролируется состоянием, а не ручными манипуляциями DOM —
  // поэтому раздел корректно сворачивается при открытии другого.
  const [openKey, setOpenKey] = useState<number | null>(null);
  const [query, setQuery] = useState(''); // фильтр-поиск по меню

  // авто-раскрытие раздела, содержащего активный URL (замена старого needToOpen-хака).
  // Берём раздел с самым длинным совпавшим url листа, чтобы /scheduler/timeline/tire
  // открывал «Шинный сервис», а не «Урегулирование убытков» (/scheduler/timeline).
  useEffect(() => {
    let best = { len: 0, key: null };
    URL.NAVI.forEach(item => {
      if (item.hide || !Array.isArray(item.kids)) { return; }
      flattenLeaves(item.kids).forEach(leaf => {
        // запрос-строка (?serviceIds=…) в матчинге не участвует: параметры
        // режимов не ломают определение раздела
        const leafPath = leaf.url.split('?')[0];
        if (leafPath && leafPath !== 'EMPTY'
          && location.pathname.includes(leafPath)
          && leafPath.length > best.len) {
          best = { len: leafPath.length, key: item.sortOrder };
        }
      });
    });
    setOpenKey(best.key);
    setQuery(''); // переход на страницу сбрасывает фильтр меню
  }, [location.pathname]);

  // нормализованные слова запроса; пустой запрос → фильтр выключен
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searching = tokens.length > 0;

  // лист совпал, если все слова запроса есть в «<раздел> <пункт>»
  const leafMatches = (item, leaf) => matchesTokens(`${ item.title } ${ leaf.title }`, tokens);
  // раздел активен (совпал), если совпал хоть один его лист; простой пункт — по своему названию
  const sectionActive = item => (Array.isArray(item.kids)
    ? flattenLeaves(item.kids).some(leaf => leafMatches(item, leaf))
    : matchesTokens(item.title, tokens));

  // динамическая ширина сайдбара: считаем по тому, что РЕАЛЬНО видно сейчас —
  // всегда по верхнеуровневым пунктам, а по подпунктам раздела — только когда он
  // раскрыт. Поэтому в свёрнутом виде сайдбар узкий и расширяется лишь при
  // открытии раздела с длинными пунктами.
  useEffect(() => {
    const compute = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) { return; }

      // берём реальный шрифт меню из отрисованного пункта
      const sample = document.querySelector('.sidebar-container .dropdown-toggle .nav-title')
        || document.querySelector('.sidebar-container .nav-title');
      const cs = sample ? getComputedStyle(sample) : null;
      const family = cs ? cs.fontFamily : 'sans-serif';
      const topFont = cs ? `${ cs.fontWeight } ${ cs.fontSize } ${ family }` : '400 16px sans-serif';
      const leafFont = `400 14px ${ family }`; // пункты внутри раздела — 14px

      const widthOf = (title, font) => {
        ctx.font = font;
        return ctx.measureText(title || '').width;
      };

      let needed = 0;
      URL.NAVI.forEach(item => {
        if (item.hide) { return; }
        if (Array.isArray(item.kids)) {
          const visible = flattenLeaves(item.kids).filter(
            leaf => checkUserPermissions(user, permissions, leaf.operation, leaf.permissions)
          );
          if (!visible.length) { return; }
          // верхний уровень виден всегда: текст + иконка + стрелка + поля
          needed = Math.max(needed, widthOf(item.title, topFont) + 84);
          // подпункты учитываем, только если раздел сейчас раскрыт
          // (при поиске раскрыты все совпавшие разделы)
          const isOpen = searching ? sectionActive(item) : openKey === item.sortOrder;
          if (isOpen) {
            visible.forEach(leaf => { needed = Math.max(needed, widthOf(leaf.title, leafFont) + 54); });
          }
        } else if (checkUserPermissions(user, permissions, item.operation, item.permissions)) {
          needed = Math.max(needed, widthOf(item.title, topFont) + 84);
        }
      });

      const width = Math.min(Math.max(Math.ceil(needed), 220), 460);
      document.documentElement.style.setProperty('--sidebar-width', `${ width }px`);
    };

    compute();
    // пересчёт после загрузки веб-шрифта (Manrope): иначе ширина считается по
    // запасному шрифту и реальный текст может не влезть → перенос стрелки
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(compute);
    }
  }, [user, permissions, openKey, query]);

  const menu: ReactNode[] = [];

  nav.forEach(item => {
    if (item.hide) {
      return;
    }

    if (Array.isArray(item.kids)) { // если есть дети, сделать дропдаун
      let hasChildren = false;
      const kids: ReactNode[] = [];

      // класс листа при активном поиске: совпавший подсвечиваем, остальные приглушаем
      const leafClass = leaf => {
        if (!searching) { return ''; }
        return leafMatches(item, leaf) ? 'nav-leaf-match' : 'nav-dimmed';
      };

      const renderLeaf = subItem => (
        <Can
          do={ subItem.permissions }
          on={ subItem.operation }
          key={ `navItem_${ item.sortOrder }_${ subItem.sortOrder }` }
        >
          <LinkContainer to={ subItem.url }>
            <Dropdown.Item className={ leafClass(subItem) }>
              <div className='d-flex justify-content-start'>
                <div> { subItem.title }</div>
              </div>
            </Dropdown.Item>
          </LinkContainer>
        </Can>
      );

      item.kids.forEach(subItem => {
        if (subItem.hide) { return; } // скрытый лист: не рендерим и в hasChildren не считаем
        if (Array.isArray(subItem.kids)) { // подгруппа с некликабельным заголовком
          const groupHasPerm = subItem.kids.some(
            leaf => !leaf.hide && checkUserPermissions(user, permissions, leaf.operation, leaf.permissions)
          );
          hasChildren = hasChildren || groupHasPerm;
          // заголовок подгруппы показываем только если есть доступ хоть к одному её пункту
          if (groupHasPerm) {
            kids.push(
              <div className='nav-subheader' key={ `navHeader_${ item.sortOrder }_${ subItem.sortOrder }` }>
                { subItem.title }
              </div>
            );
            subItem.kids.forEach(leaf => kids.push(renderLeaf(leaf)));
          }
        } else { // обычный пункт
          hasChildren = hasChildren || checkUserPermissions(user, permissions, subItem.operation, subItem.permissions);
          kids.push(renderLeaf(subItem));
        }
      });
      if (hasChildren) {
        // при поиске: совпавший раздел подсвечен и раскрыт, несовпавший — приглушён
        const active = searching && sectionActive(item);
        const dimmed = searching && !active;
        menu.push(
          <Can
            do={ item.permissions }
            on={ item.operation }
            key={ `navItem_${ item.sortOrder }` }
          >
            <Dropdown
              as={ NavItem }
              id={ `nav-dropdown${ item.sortOrder }` }
              className={ dimmed ? 'nav-dimmed' : '' }
              show={ searching ? active : openKey === item.sortOrder }
              onToggle={ (isOpen: boolean, event: any, metadata: any) => {
                // клик по пункту (навигация) и клик вне меню не должны сворачивать
                // открытый раздел — он переключается только явным кликом по заголовку
                if (metadata?.source === 'select' || metadata?.source === 'rootClose') { return; }
                setOpenKey(isOpen ? item.sortOrder : null);
              } }
            >
              <Dropdown.Toggle as={ NavLink } className={ active ? 'nav-match' : '' }>
                { item.icon && (<Icon name={ item.icon } className='nav-icon' />) }
                <span className='nav-title'>{ item.title }</span>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                { kids }
              </Dropdown.Menu>
            </Dropdown>
          </Can>
        );
      }
    } else { // детей нет = простой пункт меню
      const active = searching && sectionActive(item);
      const dimmed = searching && !active;
      menu.push(
        <Can do={ item.permissions } on={ item.operation } key={ `navItem_${ item.sortOrder }` }>
          <LinkContainer to={ item.url }>
            <Nav.Link className={ `${ dimmed ? 'nav-dimmed' : '' }${ active ? ' nav-match' : '' }` }>
              { item.icon && (<Icon name={ item.icon } className='nav-icon' />) }
              <span className='nav-title'>{ item.title }</span>
            </Nav.Link>
          </LinkContainer>
        </Can>
      );
    }
  });

  return (
    <>
      <div className='sidebar-search'>
        <Icon name='search' className='sidebar-search-icon' />
        <input
          type='text'
          className='sidebar-search-input'
          placeholder='Поиск по меню'
          value={ query }
          onChange={ e => setQuery(e.target.value) }
        />
        { query && (
          <button
            type='button'
            className='sidebar-search-clear'
            onClick={ () => setQuery('') }
            aria-label='Очистить'
          >
            <Icon name='times' />
          </button>
        ) }
      </div>
      <Nav>
        { menu }
      </Nav>
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.auth?.profile,
    permissions: state.permissions?.permissions,
  };
};

export default connect(mapStateToProps)(SideBarMenu);
